// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

interface ReadOnlySwitchProps {
  checked: boolean;
  className?: string;
}

/**
 * A switch that shows a boolean without offering to change it.
 *
 * Unlike a `disabled` switch it keeps full-strength colours — the value is the point, not the fact
 * that this particular control is inert — but it cannot be clicked or tabbed into, and it is
 * announced as read-only.
 *
 * Transitions are off on purpose. The thumb animation exists to acknowledge a click; here there is
 * no click to acknowledge, and in a data table it actively misleads: rows are keyed by index, so
 * re-sorting or filtering reuses the same DOM node and the thumb slides as if someone had toggled
 * it. Editing happens through whatever action the surrounding page provides.
 */
export function ReadOnlySwitch({ checked, className }: ReadOnlySwitchProps) {
  return (
    <Switch
      checked={checked}
      aria-readonly="true"
      tabIndex={-1}
      className={cn("pointer-events-none transition-none [&>[data-slot=switch-thumb]]:transition-none", className)}
    />
  );
}
